/**
 * 周期规则契约 —— **F 域 F1**（规格 §18 · `DR-010` · F 域 PRD §4.1 ~ §4.8）
 *
 * ## 三处刻意的形状决定
 *
 * | 决定 | 为什么 |
 * |---|---|
 * | 🔴 **不在契约里出现 `nextRunAt`(UTC 毫秒)**，只给 `nextDueDay`(`YYYY-MM-DD`) | 端侧一旦拿到毫秒就要自己算"这是哪天"，而"哪天"必须按**账本时区**判（`DR-021`：交易日 = 账本本地日历日）⇒ 端侧零日期算术，服务端把本地日**算好下发** |
 * | **`amount` 可为负**（仅 `recurring` / 🆕 `template`） | 规格 §18 原文「周期账（**可正可负**）」；负号表达在**腿方向**上（内核 `directionOfAmount`），契约层不额外加约束 |
 * | **不提供 `accountId` 的修改**（`UpdateScheduleRequest` 无该字段） | 账户与规则 **1:1** 且**规则是账户的从属**（`schedules.accountId` 有唯一索引）——改绑等于换规则，应删旧建新，否则唯一索引会在两处打架。⚠️ `template` 是 **1:N**（`DR-074`），但"改绑 = 换规则"这条对它同样成立 ⇒ 字段照样不提供 |
 */

import { z } from 'zod';
import { SchedulePolicy } from '../constants/index.js';
import { InternalCategory, LoanSubtype, RepaymentMethod } from '../enums/account.js';
import { ScheduleFrequency, ScheduleKind } from '../enums/plan.js';
import type { Cents } from '../types/brand.js';
import { DayString } from './pagination.js';

// ───────────────────────────── 请求 ─────────────────────────────

/** 规则名（可选；展示用，如「房租」「物业费」） */
export const ScheduleNameSchema = z
  .string()
  .trim()
  .min(1, '规则名不得为空')
  .max(SchedulePolicy.NameMaxLength, `规则名最长 ${SchedulePolicy.NameMaxLength} 字符`);

/**
 * `POST /api/schedules` —— 建一条周期规则（= 给某个待缴/待摊账户绑规则）。
 *
 * 🔴 **`nextRunAt` 是必填的「首次到期日」**（账本本地日）—— 这是规则唯一的**起点**来源：
 * `schedules` 没有「起始日」列（架构 §4.2 只列关键字段），首期由创建请求给定，
 * 之后的推进由内核 `nextDueDay` 完成（月末 clamp 的**锚点日**也从这里诞生）。
 */
export const CreateScheduleRequestSchema = z.object({
  /** 被绑定的账户（待缴 / 待摊 / 贷款）—— 必须与 `kind` 配套（内核判定） */
  accountId: z.string().trim().min(1, '账户 id 不得为空'),
  kind: z.enum([
    ScheduleKind.Recurring,
    ScheduleKind.Installment,
    ScheduleKind.Loan,
    ScheduleKind.Amortization,
    /** 🆕 周期记账模板（批 `132` · `DR-074`）：到期直接记一笔已发生的收支；账户是 1:N */
    ScheduleKind.Template,
  ]),
  frequency: z.enum([
    ScheduleFrequency.Daily,
    ScheduleFrequency.Weekly,
    ScheduleFrequency.Monthly,
    ScheduleFrequency.Quarterly,
    ScheduleFrequency.Yearly,
  ]),
  /** 整数分；**可为负**（`recurring` 的「可正可负」） */
  amount: z.number().int('周期金额必须是整数分'),
  /** 支出分类（可选；缺省 = 未分类，与「记一笔」同口径） */
  categoryId: z.string().trim().min(1).nullish(),
  name: ScheduleNameSchema.nullish(),
  /** 首次到期日（账本本地日历日） */
  nextRunAt: DayString,
});
export type CreateScheduleRequest = z.infer<typeof CreateScheduleRequestSchema>;

/**
 * `PATCH /api/schedules/:id` —— 改规则。
 *
 * 🔴 **「编辑不覆盖」**（规格 §18 / `AC-F1.8` / `AC-F1.9`）：改金额、频率、下次到期日**只影响未来期次**；
 * 已经生成的流水**一个字段都不动**。这条不是"实现细节"，是本域的明文口径。
 */
export const UpdateScheduleRequestSchema = z.object({
  amount: z.number().int('周期金额必须是整数分').optional(),
  frequency: z
    .enum([
      ScheduleFrequency.Daily,
      ScheduleFrequency.Weekly,
      ScheduleFrequency.Monthly,
      ScheduleFrequency.Quarterly,
      ScheduleFrequency.Yearly,
    ])
    .optional(),
  /** 改下次到期日（**主动改期**，如"这期提前到 15 号"） */
  nextRunAt: DayString.optional(),
  categoryId: z.string().trim().min(1).nullish(),
  name: ScheduleNameSchema.nullish(),
  /** 停用/启用 —— 🔴 **停用保留已生成流水**（`AC-F1.9` 同理） */
  enabled: z.boolean().optional(),
});
export type UpdateScheduleRequest = z.infer<typeof UpdateScheduleRequestSchema>;

/**
 * `POST /api/schedules/:id/settle` —— **付款销账**（F 域 PRD §4.3.3）。
 *
 * 落点：生成**转账 2 腿**（资金账户 `out` + 待缴账户 `in`，**两腿不计收支**），
 * 并把该规则生成的、仍为 `pending` 的流水置为 `settled` ⇒ **待缴账户余额归零**（`AC-F1.15`）。
 */
export const SettleScheduleRequestSchema = z.object({
  /** 付款日（账本本地日） */
  date: DayString,
  /** 从哪个账户出钱（现金 / 银行 / 电子钱包……**不得是同一个待缴账户**） */
  fromAccountId: z.string().trim().min(1, '付款账户 id 不得为空'),
  note: z.string().trim().max(200).nullish(),
});
export type SettleScheduleRequest = z.infer<typeof SettleScheduleRequestSchema>;

// ───────────────────────────── 视图 ─────────────────────────────

/**
 * 周期规则视图。
 *
 * 🔴 **日期一律是账本本地日历日字符串**（`nextDueDay` / `lastDoneDay`），
 * **不下发 UTC 毫秒**（见文件头第 1 条）。两个 `null` 的含义不同：
 * - `nextDueDay = null` ⇒ 规则**没有下一期**（未设置起点）⇒ 不生成；
 * - `lastDoneDay = null` ⇒ **从未生成过**（新规则）。
 */
export interface ScheduleView {
  id: string;
  kind: ScheduleKind;
  frequency: ScheduleFrequency;
  /** 整数分（**保留符号** —— 负号 = 方向为 `in`） */
  amount: Cents;
  categoryId: string | null;
  name: string | null;
  enabled: boolean;
  accountId: string | null;
  /** 账户展示名（服务端 join；账户已被删则为 `null`） */
  accountName: string | null;
  /** `internal` 品类（账本上"这条规则驱动哪种内部账户"的判据） */
  accountCategory: InternalCategory | null;
  /** 账户原币（金额呈现用 —— `DR-036`：金额必带币种） */
  currency: string | null;
  /** 下一次**应生成**的到期日（账本本地日）；`null` = 无下一期 */
  nextDueDay: string | null;
  /** 水位：上一期**已生成**的到期日（账本本地日）；`null` = 从未生成 */
  lastDoneDay: string | null;
  ownerId: string;
  createdAt: number;
  updatedAt: number;
}

/** `GET /api/schedules` —— 本账本全部规则（**非分页**：规则数与账户数同量级，`AccountListResult` 同处置） */
export interface ScheduleListResult {
  items: readonly ScheduleView[];
  total: number;
}

/**
 * 删除结果（`DELETE /api/schedules/:id`）。
 *
 * ⚠️ 与 `AccountDeletedView` / `PolicyDeletedView` 同处置：**形状定在契约层**，
 * 端侧禁自写（㉝ 批教训）。
 */
export interface ScheduleDeletedView {
  deleted: true;
  id: string;
  /**
   * 🔴 删除规则**不删**已生成的流水（`AC-F1.9`）⇒ 把"保住了多少笔"明确回报给 UI，
   * 让界面能提示「删除周期模板不会删除已生成的账单」。
   */
  keptTransactionCount: number;
}

/** `POST /api/schedules/:id/settle` 的结果（销账是一次真实的资金流动，须回报凭据） */
export interface SettleScheduleResultView {
  /** 🆕 生成的转账交易 id（**唯一凭据** —— 用户要能在流水里找到它） */
  transactionId: string;
  /** 被置为 `settled` 的待缴流水**笔数** */
  settledCount: number;
  /** 销账金额（整数分，正数；= 各待缴笔之和） */
  amount: Cents;
  /** 币种（= 待缴账户币种） */
  currency: string;
}

// ───────────────── 贷款计划表（批 80 · `AC-F1.18`~`F1.21` / `F1.23` · `DR-044` D7） ─────────────────
//
// 🔴 **参数只在 `accounts`（`contraTerms` 块）、结果只在 `loan_schedules`** —— 本组契约
// 下发的是**结果**（每期本息 / 状态 / 剩余），参数**原文**随视图重复下发只为端侧免二次请求，
// 修改参数的唯一入口仍是账户的 PATCH（`contract/account.ts`）。

/** 贷款期次状态（`loan_schedules.status`）：待还 / 已还 / **作废**（`AC-F2.11`：作废不进 F2 日历） */
export type LoanPeriodStatus = 'pending' | 'paid' | 'skipped';

/** 一期的拆分视图（`loan_schedules` 一行） */
export interface LoanPeriodView {
  periodIndex: number;
  /** 到期日（账本本地日历日） */
  dueDate: string;
  /** 当期应还本金（整数分，≥ 0） */
  principal: Cents;
  /** 当期应还利息（整数分，≥ 0） */
  interest: Cents;
  status: LoanPeriodStatus;
}

/** `GET /api/schedules/loan/:accountId` —— 计划表摘要 + 全部期次（期数 ≤ 1200，非分页） */
export interface LoanScheduleSummaryView {
  accountId: string;
  currency: string;
  /**
   * 参数是否齐全（`checkLoanParams()` 的判定）。
   * 🔴 `false` = **退化态**（`DR-044` §5 第 2 项：合法状态），`notReadyReason` 给出原因，
   * `periods` 为空 —— **不是错误**，端侧按提示引导补参数，不得当异常抛。
   */
  ready: boolean;
  notReadyReason: string | null;
  // 参数原文（与 `AccountView.contraTerms` 同源；`null` = 未填）
  annualRateE4: number | null;
  terms: number | null;
  method: RepaymentMethod | null;
  subtype: LoanSubtype | null;
  startDate: string | null;
  /** 债务到期日（所有 `contra` 共有；非贷款到期概念） */
  dueDate: string | null;
  /** 剩余本金（整数分，正数）= **腿派生余额的绝对值**（`AC-F1.23` 的「账户余额」侧） */
  remainingPrincipalCents: Cents;
  /** 计划表口径的剩余本金 = **Σ pending 期次本金**（`AC-F1.23` 的「计划表」侧；退化态为 `null`） */
  scheduleRemainingCents: Cents | null;
  paidPeriods: number;
  pendingPeriods: number;
  skippedPeriods: number;
  /** 下一期待还到期日（`null` = 无待还期次） */
  nextDueDate: string | null;
  /** 全部期次（含作废 —— 端侧明细页要能看见"哪些期被提前还款作废了"） */
  periods: readonly LoanPeriodView[];
}

/**
 * `POST /api/schedules/loan/:accountId/pay` —— **期次还款**（`AC-F1.19` 的落点）。
 *
 * - `periods`：要还的期次（**必填**，防"默认还清全部"的误操作；常规还款 = 最早一期）；
 *   每期可改 `principal` / `interest`（**编辑值写回计划表** —— 单真相，v1 `pay-loan` 同口径）；
 * - 流水形态：付款账户 `out` 本金（`cat-loan-principal`，**不计收支**）+ `out` 利息
 *   （`cat-loan-interest`，**计入支出**）+ 贷款账户 `in` 本金（不计收支）——
 *   即 `AC-F1.19`「利息计支出、本金不计（本金腿 `excludeFromStats=1`）」。
 */
export const PayLoanRequestSchema = z.object({
  /** 还款日（账本本地日） */
  date: DayString,
  /** 从哪个账户出钱（不得是贷款账户自身） */
  fromAccountId: z.string().trim().min(1, '付款账户 id 不得为空'),
  periods: z
    .array(
      z.object({
        periodIndex: z.number().int('期次必须是整数').min(0, '期次不得为负'),
        /** 编辑后的本金（缺省 = 计划值） */
        principal: z.number().int('本金必须是整数分').min(0, '本金不得为负').optional(),
        /** 编辑后的利息（缺省 = 计划值） */
        interest: z.number().int('利息必须是整数分').min(0, '利息不得为负').optional(),
      }),
    )
    .min(1, '至少选择一期还款'),
  note: z.string().trim().max(200).nullish(),
});
export type PayLoanRequest = z.infer<typeof PayLoanRequestSchema>;

/** `POST .../pay` 的结果 */
export interface PayLoanResultView {
  transactionId: string;
  /** 本金合计（整数分） */
  principalCents: Cents;
  /** 利息合计（整数分） */
  interestCents: Cents;
  paidPeriods: number;
  /** 还款后的计划表剩余本金（Σ 仍 `pending` 的本金） */
  remainingPrincipalCents: Cents;
  currency: string;
}

/**
 * `POST /api/schedules/loan/:accountId/prepay` —— **提前还款**（`AC-F1.20` / `AC-F1.21`）。
 *
 * 写一笔**额外本金**流水（两腿均不计收支），然后按 `mode` 重排后续期次：
 * - `shorten`（**缩期**）：月供/每期本金不变、期数缩短（`AC-F1.20`「期数可缩短」）；
 * - `reduce`（**降额**）：期数不变、每期金额下降（「金额可下降」）；
 * - `settle`（**提前结清**）：后续期次**全部作废**（`AC-F1.21`），`amount` 必须等于剩余本金。
 */
export const PrepayLoanRequestSchema = z.object({
  date: DayString,
  fromAccountId: z.string().trim().min(1, '付款账户 id 不得为空'),
  mode: z.enum(['shorten', 'reduce', 'settle']),
  /** 提前偿还的本金（正整数分） */
  amount: z.number().int('提前还款金额必须是整数分').positive('提前还款金额必须大于 0'),
  note: z.string().trim().max(200).nullish(),
});
export type PrepayLoanRequest = z.infer<typeof PrepayLoanRequestSchema>;

/** `POST .../prepay` 的结果 */
export interface PrepayLoanResultView {
  transactionId: string;
  mode: 'shorten' | 'reduce' | 'settle';
  /** 重算的后续期次数（`settle` = 0） */
  rescheduledPeriods: number;
  /** 作废的期次数（`AC-F1.21`；`reduce` 恒为 0） */
  voidedPeriods: number;
  /** 重排后的剩余本金（整数分） */
  remainingPrincipalCents: Cents;
  /** 重排后的剩余期数（`settle` = 0） */
  remainingPeriods: number;
  currency: string;
}

// ═════════════ 贷款卡面进度（`AC-Q4.5` · 批 81 · `DR-046` D6） ═════════════

/**
 * 🆕 贷款账户的**卡面进度**（`GET /api/loans/progress` 的列表投影）。
 *
 * ## 🔴 为什么单独一个批量接口，而不是塞进 `AccountView`
 *
 * 1. **跨域方向**：把 `loan_schedules` 的聚合塞进 `accounts` 的视图，等于让 `account` 域
 *    反向依赖 `schedule` 域的**数据**（`DR-021` §6.1 跨域只引 `modules/<域>/index.ts`）；
 * 2. **N+1**：L2 要一次画 N 张卡，`LoanScheduleSummaryView` 是**单账户**接口（批 `80`）。
 *
 * ## 🔴 只给「期数进度」，**不给金额进度**
 *
 * | 类型（`contraKind`） | 进度 | 为什么 |
 * |---|---|---|
 * | `loan` | ✅ `paidPeriods / terms` | 期次在 `loan_schedules` 里，可数 |
 * | `receivable` / `payable` / `advance` / `reimburse` | ❌ **留白** | 🔴 它们的"原始总额"在 v2 **无载体**：批 `78` 把 `initial_balance` 落成**迁移时点剩余**（不是本金），`contra_settlements` 全账本只有 2 行 ⇒ 算 `(原始 − 剩余) / 原始` 会**编出一个分母**。宁可留白 |
 *
 * ⚠️ 故端侧规则：`terms === null` 或该账户不在本列表里 ⇒ **不显示进度**（`AC-Q4.5` 的"空则留白"同一处置）。
 */
export interface LoanProgressView {
  accountId: string;
  /** 已还期数（`loan_schedules.status='paid'`） */
  paidPeriods: number;
  /** 未还期数（`status='pending'`） */
  pendingPeriods: number;
  /** 已作废期数（提前结清 / 缩期后的多余期次，`status='skipped'`） */
  skippedPeriods: number;
  /** 下一期待还到期日；`null` = 无待还（含已结清） */
  nextDueDate: string | null;
  /** 计划表口径剩余本金（Σ `pending` 期次本金）；`null` = 该账户**还没有计划表** */
  scheduleRemainingCents: Cents | null;
}

/** `GET /api/loans/progress` —— 本账本**全部**贷款账户的卡面进度（不分页：贷款账户是个位数） */
export interface LoanProgressListResult {
  items: readonly LoanProgressView[];
}
